import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"

// Lazy-loadable locale loaders. Add more as needed (e.g. de, fr).
const localeLoaders = {
  hr: () => import("flatpickr/dist/esm/l10n/hr.js").then((m) => m.Croatian),
  de: () => import("flatpickr/dist/esm/l10n/de.js").then((m) => m.German),
  fr: () => import("flatpickr/dist/esm/l10n/fr.js").then((m) => m.French),
}

/**
 * Wraps an input with Flatpickr for date (or datetime) picking.
 * Locale is configurable; defaults to "en" (Flatpickr built-in).
 *
 * Usage:
 *   <input type="text"
 *          data-controller="datepicker"
 *          data-datepicker-locale-value="en"
 *          data-datepicker-date-format-value="Y-m-d"
 *          data-datepicker-allow-input-value="false"
 *          name="model[date_attr]"
 *          id="model_date_attr" />
 *
 * To use the current app locale (e.g. from I18n), set the value in the view:
 *   data-datepicker-locale-value="<%= I18n.locale.to_s.split('-').first %>"
 *
 * Values:
 *   - locale: "en" (default) or "hr", "de", "fr", etc. (add loader in localeLoaders if needed)
 *   - dateFormat: Flatpickr format, e.g. "Y-m-d" (default), "Y-m-d H:i" for datetime
 *   - allowInput: allow typing in the input (default false)
 *   - enableTime: show time picker (default false); use with dateFormat "Y-m-d H:i" for datetime
 *   - minDate, maxDate: optional, ISO date string or "today"
 */
export default class extends Controller {
  static values = {
    locale: { type: String, default: "en" },
    dateFormat: { type: String, default: "Y-m-d" },
    allowInput: { type: Boolean, default: false },
    enableTime: { type: Boolean, default: false },
    minDate: { type: String, default: "" },
    maxDate: { type: String, default: "" },
  }

  async connect() {
    const config = {
      dateFormat: this.dateFormatValue,
      allowInput: this.allowInputValue,
      enableTime: this.enableTimeValue,
      time_24hr: true,
    }

    if (this.minDateValue) {
      config.minDate = this.minDateValue === "today" ? new Date() : this.minDateValue
    }
    if (this.maxDateValue) {
      config.maxDate = this.maxDateValue === "today" ? new Date() : this.maxDateValue
    }

    const localeCode = (this.localeValue || "en").toLowerCase()
    if (localeCode !== "en" && localeLoaders[localeCode]) {
      try {
        config.locale = await localeLoaders[localeCode]()
      } catch (_e) {
        // fallback: use default (en)
      }
    }

    this.instance = flatpickr(this.element, config)
  }

  disconnect() {
    if (this.instance) {
      this.instance.destroy()
      this.instance = null
    }
  }
}
